import { createStore } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { User } from "@/gql/auth";
import { storage } from "@/lib/storage";

export interface UserPreferences {
	theme: "light" | "dark" | "system";
	language: string;
	currency: string;
	notifications: {
		email: boolean;
		push: boolean;
		sms: boolean;
	};
}

export interface UserState {
	// User authentication state
	isAuthenticated: boolean;
	user: User | null;

	// User preferences
	preferences: UserPreferences;

	// Actions
	setAuthenticated: (isAuthenticated: boolean) => void;
	setUser: (user: User | null) => void;
	updatePreferences: (preferences: Partial<UserPreferences>) => void;
	logout: () => void;
}

export interface UserStoreProps {
	initialUser?: User | null;
	initialPreferences?: Partial<UserPreferences>;
}

const defaultPreferences: UserPreferences = {
	theme: "system",
	language: "en",
	currency: "INR",
	notifications: {
		email: true,
		push: true,
		sms: false,
	},
};

export const createUserStore = ({ initialUser, initialPreferences }: UserStoreProps = {}) =>
	createStore<UserState>()(
		devtools(
			subscribeWithSelector(
				immer(set => ({
					isAuthenticated: !!initialUser,
					user: initialUser || null,
					preferences: { ...defaultPreferences, ...initialPreferences },

					setAuthenticated: isAuthenticated =>
						set(state => {
							state.isAuthenticated = isAuthenticated;
						}),

					setUser: user =>
						set(state => {
							state.user = user;
							state.isAuthenticated = !!user;
						}),

					updatePreferences: preferences =>
						set(state => {
							state.preferences = { ...state.preferences, ...preferences };
							// Save to storage
							if (typeof window !== "undefined") {
								storage.setItem("settlin_user_preferences", state.preferences);
							}
						}),

					logout: () =>
						set(state => {
							state.isAuthenticated = false;
							state.user = null;
							// Clear user data from storage
							if (typeof window !== "undefined") {
								storage.removeItem("settlin_auth_token");
								storage.removeItem("settlin_user_preferences");
							}
						}),
				})),
			),
			{ name: "user-store" },
		),
	);
